import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { Pagination } from '../ui/Pagination';
import { ProfileListItem } from './ProfileListItem';
import { Building2, Clock, CheckCircle, UserCheck } from 'lucide-react';
import { IntelligenceProfile, ProfileStatusFilter, ProfilesPagination } from '../../types';

// Simplified interface - removed complex progress tracking properties

interface ProfilesListProps {
  profiles: IntelligenceProfile[];
  activeTab: ProfileStatusFilter;
  pagination: ProfilesPagination;
  executingOrchestrator: string | null;
  onExecuteOrchestrator: (profile: IntelligenceProfile) => void;
  onReprocessAnalysis?: (profile: IntelligenceProfile) => void;
  onPageChange: (page: number) => void;
  className?: string;
}

export const ProfilesList: React.FC<ProfilesListProps> = ({
  profiles,
  activeTab,
  pagination,
  executingOrchestrator,
  onExecuteOrchestrator,
  onReprocessAnalysis,
  onPageChange,
  className
}) => {
  const getTabIcon = (tab: ProfileStatusFilter) => {
    switch (tab) {
      case 'all':
        return Building2
      case 'pending':
        return Clock
      case 'processed':
        return CheckCircle
      case 'assigned':
        return UserCheck
      default:
        return Building2
    }
  };

  return (
    <div className={className}>
      {/* Profiles List */}
      <div className="space-y-6">
        {profiles.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              {React.createElement(getTabIcon(activeTab), { 
                className: "h-12 w-12 mx-auto text-gray-400 mb-4" 
              })}
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {activeTab === 'all' ? '' : activeTab} profiles found
              </h3>
              <p className="text-gray-600">
                {activeTab === 'all' 
                  ? 'Intelligence profiles will appear here once onboarding is completed.'
                  : `No profiles with ${activeTab} status at the moment.`
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          profiles.map((profile) => (
            <ProfileListItem
              key={profile.id}
              profile={profile}
              executingOrchestrator={executingOrchestrator}
              onExecuteOrchestrator={onExecuteOrchestrator}
              onReprocessAnalysis={onReprocessAnalysis}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-8">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={onPageChange}
            showInfo={true}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            className="justify-center"
          />
        </div>
      )}
    </div>
  );
};